/**
 * PURPOSE: Modelo puro de los trazos del pad de firma (FTR-0062): coordenadas, dibujo suavizado y exportación del PNG final.
 * RESPONSIBILITIES:
 *  - Sí: convertir la posición del puntero a coordenadas normalizadas del lienzo con getBoundingClientRect (vale con cualquier
 *    devicePixelRatio, scroll, zoom del navegador o transform de un ancestro: se mide el rectángulo real en cada evento).
 *  - Sí: dibujar los trazos con curvas que pasan por TODOS los puntos recibidos (Catmull-Rom → Bézier): la tinta cae sobre el cursor.
 *  - Sí: exportar la firma a un PNG transparente, recortado al trazo y a la escala de su casilla en la hoja, con tinta del PDF.
 *  - No: no toca el DOM ni React (el lienzo y el contexto se inyectan).
 * COLLABORATORS:
 *  - SignatureCanvas.jsx
 * NOTES:
 *  - Los trazos se guardan normalizados (0..1 del lienzo): al cambiar de tamaño se redibujan sin deformarse (el lienzo tiene proporción fija).
 *  - La línea del PDF es fija (~0.8 pt, como un bolígrafo): una firma grande dibujada en el pad no queda como un trazo de pelo al reducirse.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/signatureStrokes.js
 */

/** Tinta de la planilla FEDUP (INK del renderer: rgb(0.06, 0.1, 0.38)). */
export const SIGNATURE_INK = "rgb(15, 26, 97)";
/** Grosor del trazo en el pad, en px CSS. */
export const PAD_LINE_WIDTH_CSS = 2.4;
/** Resolución del PNG exportado (px por punto PDF de la casilla) y grosor del trazo en el PDF. */
export const EXPORT_PX_PER_PT = 6;
export const EXPORT_LINE_WIDTH_PT = 0.8;
/** Margen de la regla de incrustación del backend (SIGNATURE_MARGIN) y del recorte del servidor (padding de signaturePng.js). */
const SHEET_MARGIN_PT = 0.6;
const EXPORT_PADDING_PX = 4;
const MAX_EXPORT_SIDE = 3000;

/** Punto normalizado (0..1) desde coordenadas de viewport y el rectángulo actual del lienzo. */
export function toNormalizedPoint(clientX, clientY, rect) {
  const w = rect?.width || 0;
  const h = rect?.height || 0;
  if (!(w > 0) || !(h > 0)) return null;
  return { x: (clientX - rect.left) / w, y: (clientY - rect.top) / h };
}

/** Añade `point` al trazo salvo que esté a menos de `minDistance` (en unidades normalizadas × tamaño) del último. */
export function pushPoint(stroke, point, { width = 1, height = 1, minDistance = 0.35 } = {}) {
  if (!point) return false;
  const last = stroke[stroke.length - 1];
  if (last) {
    const dx = (point.x - last.x) * width;
    const dy = (point.y - last.y) * height;
    if (dx * dx + dy * dy < minDistance * minDistance) return false;
  }
  stroke.push(point);
  return true;
}

export function strokesAreEmpty(strokes) {
  return !Array.isArray(strokes) || strokes.every((s) => !s || s.length === 0);
}

/**
 * Dibuja los trazos en `ctx`. `map(p)` lleva un punto normalizado a píxeles del contexto. Un trazo de un solo punto es un punto redondo;
 * los demás, una curva Catmull-Rom (uniforme) que pasa por cada punto recibido.
 */
export function drawStrokes(ctx, strokes, { map, lineWidth, color = SIGNATURE_INK }) {
  if (!ctx) return;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  for (const stroke of strokes || []) {
    if (!stroke?.length) continue;
    const pts = stroke.map(map);
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 0; i < pts.length - 1; i += 1) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      ctx.bezierCurveTo(
        p1.x + (p2.x - p0.x) / 6,
        p1.y + (p2.y - p0.y) / 6,
        p2.x - (p3.x - p1.x) / 6,
        p2.y - (p3.y - p1.y) / 6,
        p2.x,
        p2.y
      );
    }
    ctx.stroke();
  }
}

/** Rectángulo de los trazos en px CSS del pad (o null si no hay trazos). */
export function strokesBounds(strokes, cssWidth, cssHeight) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const stroke of strokes || []) {
    for (const p of stroke || []) {
      const x = p.x * cssWidth;
      const y = p.y * cssHeight;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
}

/**
 * Plan de exportación: escala (px del PNG por px CSS del pad), grosor del trazo y tamaño del lienzo. Con `inkBoxPt` (zona de tinta de la
 * casilla en puntos) se calcula la escala a la que el backend la va a incrustar para que el trazo mida ~EXPORT_LINE_WIDTH_PT en el PDF.
 */
export function planSignatureExport(strokes, { cssWidth, cssHeight, inkBoxPt = null }) {
  const bounds = strokesBounds(strokes, cssWidth, cssHeight);
  if (!bounds) return null;
  let scale;
  let lineWidth;
  if (inkBoxPt) {
    const availW = Math.max(1, inkBoxPt.x1 - inkBoxPt.x0 - 2 * SHEET_MARGIN_PT);
    const availH = Math.max(1, inkBoxPt.y1 - inkBoxPt.y0 - 2 * SHEET_MARGIN_PT);
    const ptPerCss = Math.min(availW / bounds.width, availH / bounds.height);
    scale = ptPerCss * EXPORT_PX_PER_PT;
    lineWidth = EXPORT_LINE_WIDTH_PT * EXPORT_PX_PER_PT;
  } else {
    scale = 2;
    lineWidth = PAD_LINE_WIDTH_CSS * 2;
  }
  // Tope de tamaño (firmas enormes): se reduce la escala manteniendo el grosor relativo.
  const longest = Math.max(bounds.width, bounds.height) * scale;
  if (longest > MAX_EXPORT_SIDE) {
    const f = MAX_EXPORT_SIDE / longest;
    scale *= f;
    lineWidth *= f;
  }
  const pad = EXPORT_PADDING_PX + lineWidth / 2 + 1;
  const width = Math.max(1, Math.ceil(bounds.width * scale + 2 * pad));
  const height = Math.max(1, Math.ceil(bounds.height * scale + 2 * pad));
  const map = (p) => ({ x: (p.x * cssWidth - bounds.minX) * scale + pad, y: (p.y * cssHeight - bounds.minY) * scale + pad });
  return { bounds, scale, lineWidth, width, height, pad, map };
}

/**
 * PNG (data URL) de la firma: fondo transparente, recortado al trazo, tinta de la planilla. `createCanvas(w, h)` devuelve un lienzo
 * (inyectable en tests). Devuelve { dataUrl, width, height } o null si no hay trazos.
 */
export function exportSignaturePng(strokes, { cssWidth, cssHeight, inkBoxPt = null, createCanvas }) {
  const plan = planSignatureExport(strokes, { cssWidth, cssHeight, inkBoxPt });
  if (!plan) return null;
  const canvas = createCanvas(plan.width, plan.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, plan.width, plan.height);
  drawStrokes(ctx, strokes, { map: plan.map, lineWidth: plan.lineWidth });
  return { dataUrl: canvas.toDataURL("image/png"), width: plan.width, height: plan.height };
}
